// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FilmReel, MaskHappy, Sparkle, Lightning, GoogleLogo, ArrowRight, Waveform, MusicNotes, Microphone } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import Seo from "@/components/Seo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import FeaturedSounds from "@/components/FeaturedSounds";

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { lang, t } = useLang();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page">
      <Seo lang={lang} path="/" />
      {/* Top nav */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-6 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[color:var(--accent)] grid place-items-center">
            <Waveform size={18} weight="fill" color="white" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">AuraFX</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            data-testid="nav-login-btn"
            onClick={handleLogin}
            className="text-sm text-white/80 hover:text-white transition-colors"
          >
            {t("signIn")}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" />
            <span className="text-xs text-white/70 font-mono-num uppercase tracking-widest">SFXAura · Neural Audio Engine</span>
          </div>
          {lang === "en" ? (
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Generate <span className="italic font-light">cinema-grade</span>
              <br />
              sound effects
              <br />
              <span className="text-[color:var(--accent)]">from a single prompt.</span>
            </h1>
          ) : (
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              {t("heroMain")}
              <br />
              <span className="text-[color:var(--accent)]">{t("heroAccent")}</span>
            </h1>
          )}
          <p className="mt-8 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
            {t("heroSubtitle")}
          </p>
          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <button
              data-testid="hero-get-started-btn"
              onClick={handleLogin}
              className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium"
            >
              <GoogleLogo size={18} weight="bold" /> {t("ctaGoogle")}
              <ArrowRight size={18} weight="bold" />
            </button>
            <a
              href="#categories"
              className="text-sm text-white/70 hover:text-white transition-colors px-4 py-3.5"
            >
              {t("exploreCategories")}
            </a>
          </div>
          <div className="mt-12 flex items-center gap-8 text-xs font-mono-num uppercase tracking-widest text-white/40">
            <span>{t("stat1")}</span>
            <span>·</span>
            <span>{t("stat2")}</span>
            <span>·</span>
            <span>{t("stat3")}</span>
          </div>
        </div>

        {/* Hero visual */}
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 relative">
            <img
              src="/hero-bird.png"
              alt="AuraFX"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg)] via-transparent to-transparent" />
            <div className="absolute left-5 right-5 bottom-5 glass rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[color:var(--accent)] grid place-items-center">
                <Sparkle size={18} weight="fill" color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white/50 font-mono-num uppercase tracking-widest">Now playing</p>
                <p className="text-sm font-medium truncate">Heavy wooden door creaking open in a haunted mansion</p>
              </div>
              <span className="text-xs font-mono-num text-white/60">00:04.2</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories teaser */}
      <section id="categories" className="relative z-10 max-w-[1400px] mx-auto px-8 pb-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-mono-num uppercase tracking-widest text-white/40 mb-3">{t("catKicker")}</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
              {t("catTitle")} <span className="italic font-light">{t("catTitleItalic")}</span>.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { i: MaskHappy, id: "theater", n: t("catTheater"), d: t("catTheaterD") },
            { i: FilmReel, id: "cinema", n: t("catCinema"), d: t("catCinemaD") },
            { i: Sparkle, id: "cartoon", n: t("catCartoon"), d: t("catCartoonD") },
            { i: Lightning, id: "action", n: t("catAction"), d: t("catActionD") },
          ].map(({ i: Icon, id, n, d }) => (
            <div
              key={id}
              data-testid={`category-card-${id}`}
              className="group relative rounded-xl border border-white/10 bg-[color:var(--surface)] p-6 hover:border-white/25 transition-colors"
            >
              <Icon size={28} weight="duotone" className="text-[color:var(--accent)] mb-4" />
              <h3 className="font-display font-semibold text-lg">{n}</h3>
              <p className="text-sm text-white/50 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <FeaturedSounds />

      {/* SEO: what you can create */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-8 pb-28">
        <p className="text-xs font-mono-num uppercase tracking-widest text-white/40 mb-3">{t("seoUsesKicker")}</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-10">{t("seoUsesTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { i: Waveform, T: t("seoUse1T"), D: t("seoUse1D") },
            { i: MusicNotes, T: t("seoUse2T"), D: t("seoUse2D") },
            { i: Microphone, T: t("seoUse3T"), D: t("seoUse3D") },
          ].map(({ i: Icon, T, D }) => (
            <div key={T} className="rounded-xl border border-white/10 bg-[color:var(--surface)] p-6">
              <Icon size={26} weight="duotone" className="text-[color:var(--accent)] mb-4" />
              <h3 className="font-display font-semibold text-lg">{T}</h3>
              <p className="text-sm text-white/50 mt-2 leading-relaxed">{D}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SEO: FAQ */}
      <section className="relative z-10 max-w-[900px] mx-auto px-8 pb-32" data-testid="faq-section">
        <p className="text-xs font-mono-num uppercase tracking-widest text-white/40 mb-3">{t("faqKicker")}</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-8">{t("faqTitle")}</h2>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <details key={n} className="group rounded-xl border border-white/10 bg-[color:var(--surface)] px-6 py-4">
              <summary className="cursor-pointer list-none flex items-center justify-between font-display font-semibold text-base text-white/90">
                {t(`faq${n}q`)}
                <span className="text-[color:var(--accent)] transition-transform group-open:rotate-45 text-xl leading-none ml-4">+</span>
              </summary>
              <p className="text-sm text-white/55 mt-3 leading-relaxed">{t(`faq${n}a`)}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-8 px-8 text-center">
        <p className="text-xs font-mono-num uppercase tracking-widest text-white/30">
          {t("footer")}
        </p>
      </footer>
    </div>
  );
}
